import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import { Icon } from './Icon';
import { Button } from '../../components/arc/button/button';
import { Card } from '../../components/arc/card/card';
import { MetricCard } from '../../components/arc/metric-card/metric-card';
import { PageState } from '../../components/arc/page-state/page-state';
import { ArcPanel, ArcSelect, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Progress } from '../../components/arc/progress/progress';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import {
  getCampaigns, getCampaignDetail, getCampaignJobs, archiveCampaign, duplicateCampaign,
  updateCampaign, getCampaignConversions, type CampaignConversions,
  type CampaignListItem, type CampaignDetail, type CampaignJob, type CampaignStatus, type Channel,
} from '../../api/marketing';
import { CampaignWizard, CampaignTypeChoice } from './CampaignWizard';
import { useTabParam } from '../../shell/useTabParam';
import { SandboxHtml } from '../../shell/SandboxHtml';
import { confirmDialog } from '../../shell/dialogs';
import './marketing-campaigns-arc.css';

const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const LIMITS = [25, 50, 100, 200, 0];

const STATUS_META: Record<CampaignStatus, { label: string; bg: string; color: string }> = {
  draft:     { label: 'Koncept',     bg: '#eef2f7', color: '#475569' },
  scheduled: { label: 'Naplánováno', bg: '#e0f2fe', color: '#0369a1' },
  running:   { label: 'Odesílá se',  bg: '#fef9c3', color: '#854d0e' },
  paused:    { label: 'Pozastaveno', bg: '#fee2e2', color: '#b91c1c' },
  sent:      { label: 'Odeslaná',    bg: '#ede9fe', color: '#5b3f96' },
  completed: { label: 'Dokončená',   bg: '#ede9fe', color: '#5b3f96' },
  archived:  { label: 'Archivovaná', bg: '#f3f4f6', color: '#6b7280' },
};

const CHANNEL_ICON: Record<Channel, string> = {
  mail: 'mail', sms: 'message-square', wa: 'message-circle', modal: 'square', notif: 'bell',
};

const TYPE_META: Record<string, { icon: string; label: string }> = {
  oneshot:   { icon: 'send',       label: 'Jednorázová' },
  automated: { icon: 'zap',        label: 'Automatizovaná' },
  recurring: { icon: 'repeat',     label: 'Opakovaná' },
  lp:        { icon: 'layout',     label: 'Landing page' },
};
function TypeIcon({ type }: { type: string }) {
  const m = TYPE_META[type] ?? { icon: 'circle', label: type };
  return <span title={m.label} className="text-muted d-inline-flex align-items-center"><Icon name={m.icon} size={16} /></span>;
}

function campaignInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'K';
}

const CHANNEL_LABEL: Record<Channel, string> = {
  mail: 'E-mail', sms: 'SMS', wa: 'WhatsApp', modal: 'Modal', notif: 'Notifikace',
};

type Tab = 'active' | 'inactive' | 'draft' | 'archived';
const KAMPANE_TABS: readonly Tab[] = ['active', 'inactive', 'draft', 'archived'];
const TAB_STATUSES: Record<Tab, CampaignStatus[]> = {
  active: ['running', 'scheduled', 'paused'],
  inactive: ['sent', 'completed'],
  draft: ['draft'],
  archived: ['archived'],
};
const TAB_LABEL: Record<Tab, string> = {
  active: 'Aktivní',
  inactive: 'Dokončené',
  draft: 'Koncepty',
  archived: 'Archivované',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function MarketingKampanePage() {
  const initial = new URLSearchParams(window.location.search);
  const [selected, setSelected] = useState<number | null>(() => {
    const p = initial.get('campaign');
    return p && !Number.isNaN(Number(p)) ? Number(p) : null;
  });
  const [creating, setCreating] = useState<boolean>(() => initial.get('new') === '1');
  const [choosing, setChoosing] = useState(false);
  const [editing, setEditing] = useState<number | null>(() => {
    const p = initial.get('edit');
    return p && !Number.isNaN(Number(p)) ? Number(p) : null;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selected, creating, editing]);

  if (creating) {
    return <CampaignWizard onDone={() => { setCreating(false); syncParam('new', null); }} />;
  }
  if (editing != null) {
    return <CampaignWizard editId={editing} onDone={() => { setEditing(null); syncParam('edit', null); }} />;
  }
  if (selected != null) {
    return <CampaignDetailView id={selected} onBack={() => { setSelected(null); syncParam('campaign', null); }}
      onEdit={(eid) => { setSelected(null); syncParam('campaign', null); setEditing(eid); syncParam('edit', String(eid)); }} />;
  }
  return (
    <>
      <CampaignListView
        onOpen={(id) => { setSelected(id); syncParam('campaign', String(id)); }}
        onNew={() => setChoosing(true)}
        onEdit={(id) => { setEditing(id); syncParam('edit', String(id)); }}
      />
      {choosing && (
        <CampaignTypeChoice
          onClose={() => setChoosing(false)}
          onPick={() => { setChoosing(false); setCreating(true); syncParam('new', '1'); }}
        />
      )}
    </>
  );
}

function syncParam(key: string, value: string | null) {
  const url = new URL(window.location.href);
  url.searchParams.delete(key === 'new' ? 'campaign' : 'new');
  if (value == null) url.searchParams.delete(key);
  else url.searchParams.set(key, value);
  window.history.replaceState({}, '', url.toString());
}

function StatusBadge({ status, pct, isModal }: { status: CampaignStatus; pct?: number; isModal?: boolean }) {
  const m = STATUS_META[status];
  const label = isModal ? (status === 'running' ? 'Zobrazuje se' : status === 'completed' ? 'Ukončeno' : m.label) : m.label;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px',
      borderRadius: 999, fontSize: '0.72rem', fontWeight: 500, background: m.bg, color: m.color,
    }}>{label}{pct != null ? ` ${pct} %` : ''}</span>
  );
}

function CampaignListView({ onOpen, onNew, onEdit }: { onOpen: (id: number) => void; onNew: () => void; onEdit: (id: number) => void }) {
  const [all, setAll] = useState<CampaignListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam<Tab>('tab', KAMPANE_TABS, 'active');
  const [fType, setFType] = useState('');
  const [fCountry, setFCountry] = useState('');
  const [fChannel, setFChannel] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(50);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCampaigns()
      .then((d) => !cancelled && setAll(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const anyInFlight = !!all?.some((c) => c.status === 'running' || c.status === 'scheduled');
  useEffect(() => {
    if (!anyInFlight) return;
    const t = setInterval(() => { getCampaigns().then(setAll).catch(() => undefined); }, 5000);
    return () => clearInterval(t);
  }, [anyInFlight]);

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { active: 0, inactive: 0, draft: 0, archived: 0 };
    if (all) for (const x of all) {
      for (const t of Object.keys(TAB_STATUSES) as Tab[]) if (TAB_STATUSES[t].includes(x.status)) c[t]++;
    }
    return c;
  }, [all]);

  const filtered = useMemo(() => {
    if (!all) return [];
    return all.filter((x) => {
      if (!TAB_STATUSES[tab].includes(x.status)) return false;
      if (fType && x.type !== fType) return false;
      if (fCountry && x.country !== fCountry) return false;
      if (fChannel && !x.channels.includes(fChannel as Channel)) return false;
      if (search && !x.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [all, tab, fType, fCountry, fChannel, search]);
  const rows = useMemo(() => (limit > 0 ? filtered.slice(0, limit) : filtered), [filtered, limit]);
  const tableRows = rows.map((campaign, index) => {
    const previous = rows.slice(index + 1).find((item) => item.country === campaign.country && item.ctr != null);
    return {
      ...campaign,
      market: campaign.country ?? '',
      recipientCount: campaign.recipients || campaign.recipientsEstimate || 0,
      openCount: campaign.opensUnique,
      clickCount: campaign.uniqueClickers,
      ctrValue: campaign.ctr ?? -1,
      deltaCtr: campaign.ctr != null && previous?.ctr != null ? Math.round((campaign.ctr - previous.ctr) * 10) / 10 : null,
      conversions: '—',
      detail: campaign.id,
    };
  });
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'name', label: 'Kampaň', width: 290, render: (_, campaign) => <button type="button" className="rondo-arc-campaign-name" onClick={() => onOpen(campaign.id)}><span className={`campaign-mark type-${campaign.type}`}>{campaignInitials(campaign.name)}</span><span><strong>{campaign.name}</strong><small>{TYPE_META[campaign.type]?.label ?? campaign.type} · {fmtDateTime(campaign.sentAt ?? campaign.scheduledAt ?? campaign.createdAt)}</small></span></button> },
    { key: 'market', label: 'Trh a kanál', width: 130, render: (_, campaign) => <span className="rondo-arc-campaign-market">{campaign.country && <span>{FLAG[campaign.country] ?? ''} {campaign.country}</span>}<small>{campaign.channels.map((channel) => CHANNEL_LABEL[channel]).join(' · ') || 'Bez kanálu'}</small></span> },
    { key: 'status', label: 'Stav', width: 128, render: (_, campaign) => <span className="rondo-arc-campaign-status"><StatusBadge status={campaign.status} isModal={campaign.channels.includes('modal')} />{campaign.status === 'scheduled' && <small>{fmtDateTime(campaign.scheduledAt)}</small>}{!campaign.channels.includes('modal') && (campaign.status === 'running' || campaign.status === 'paused') && campaign.recipients > 0 && <small>{Math.min(100, Math.round((campaign.jobsDone / campaign.recipients) * 100))} % hotovo</small>}</span> },
    { key: 'recipientCount', label: 'Příjemci', numeric: true, width: 96, render: (_, campaign) => campaign.recipientCount.toLocaleString('cs-CZ') },
    { key: 'openCount', label: 'Otevření', numeric: true, width: 96, render: (_, campaign) => campaign.openCount.toLocaleString('cs-CZ') },
    { key: 'clickCount', label: 'Prokliky', numeric: true, width: 96, render: (_, campaign) => campaign.clickCount.toLocaleString('cs-CZ') },
    { key: 'ctrValue', label: 'CTR', numeric: true, width: 72, render: (_, campaign) => campaign.ctr != null ? `${campaign.ctr} %` : '—' },
    ...(compare ? [{ key: 'deltaCtr', label: 'Δ CTR', numeric: true, width: 78, render: (_: unknown, campaign: TableRow) => campaign.deltaCtr == null ? '—' : `${campaign.deltaCtr > 0 ? '+' : ''}${campaign.deltaCtr} %` } satisfies DataColumn<TableRow>] : []),
    { key: 'conversions', label: 'Konverze', width: 86, sortable: false },
    { key: 'detail', label: '', width: 56, sortable: false, render: (_, campaign) => <span className="rondo-arc-campaign-actions"><button type="button" aria-label={`Otevřít kampaň ${campaign.name}`} onClick={() => onOpen(campaign.id)}><Icon name="chevron-right" size={16} /></button>{campaign.status === 'draft' && <button type="button" aria-label={`Upravit kampaň ${campaign.name}`} onClick={() => onEdit(campaign.id)}><Icon name="edit-2" size={15} /></button>}</span> },
  ];

  if (loading) return <PageState title="Načítám kampaně" loading />;
  if (error) return <PageState title="Kampaně se nepodařilo načíst" message={error} />;

  return (
    <section className="rondo-campaign-list marketing-campaign-list rondo-arc-campaign-list p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">MARKETING</div><h1>Kampaně</h1><p>Přehled komunikace napříč kanály</p></div><Button variant="primary" size="sm" onClick={onNew}>Nová kampaň</Button></header>
      <ArcTabs label="Stav kampaní" items={KAMPANE_TABS.map((value) => ({ value, label: <>{TAB_LABEL[value]} <span className="rondo-arc-campaign-tab-count">{counts[value]}</span></> }))} value={tab} onChange={setTab} className="rondo-arc-campaign-tabs" />

      <ArcToolbar className="rondo-arc-campaign-toolbar" label="Filtry kampaní" end={<div className="rondo-arc-campaign-toolbar-end"><input type="search" aria-label="Hledat kampaň" placeholder="Hledat kampaň…" value={search} onChange={(event) => setSearch(event.target.value)} /><span>{rows.length.toLocaleString('cs-CZ')} z {filtered.length.toLocaleString('cs-CZ')}</span></div>}>
        <ArcSelect label="Typ" value={fType} onChange={setFType} options={[{ value: '', label: 'Všechny' }, { value: 'oneshot', label: 'Jednorázová' }, { value: 'automated', label: 'Automatizovaná' }, { value: 'recurring', label: 'Opakovaná' }, { value: 'lp', label: 'LP' }]} />
        <ArcSelect label="Země" value={fCountry} onChange={setFCountry} options={[{ value: '', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ' }, { value: 'SK', label: '🇸🇰 SK' }, { value: 'RO', label: '🇷🇴 RO' }]} />
        <ArcSelect label="Kanál" value={fChannel} onChange={setFChannel} options={[{ value: '', label: 'Všechny' }, { value: 'mail', label: 'E-mail' }, { value: 'sms', label: 'SMS' }, { value: 'modal', label: 'Modal' }, { value: 'notif', label: 'Notifikace' }, { value: 'wa', label: 'WhatsApp' }]} />
        <ArcSelect label="Limit" value={String(limit)} onChange={(value) => setLimit(Number(value))} options={LIMITS.map((item) => ({ value: String(item), label: item === 0 ? 'Vše' : String(item) }))} />
        <label className="rondo-arc-campaign-compare"><input type="checkbox" checked={compare} onChange={(event) => setCompare(event.target.checked)} /><span>Srovnání</span></label>
        <Button variant="ghost" size="sm" onClick={() => { setFType(''); setFCountry(''); setFChannel(''); setSearch(''); setCompare(false); }}>Vyčistit</Button>
      </ArcToolbar>

      <div className="rondo-arc-campaign-table">
        <SortableDataTable rows={tableRows} columns={columns} rowKey="id" caption="Marketingové kampaně" emptyMessage="Žádná kampaň neodpovídá filtru." />
      </div>
    </section>
  );
}

type DetailTab = 'prehledy' | 'udaje' | 'html' | 'prijemci' | 'konverze';
const DETAIL_TABS: readonly DetailTab[] = ['prehledy', 'udaje', 'html', 'prijemci', 'konverze'];

function CampaignDetailView({ id, onBack, onEdit }: { id: number; onBack: () => void; onEdit: (id: number) => void }) {
  const [data, setData] = useState<CampaignDetail | null>(null);
  const [jobs, setJobs] = useState<CampaignJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam<DetailTab>('ctab', DETAIL_TABS, 'prehledy');
  const [archiving, setArchiving] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [conv, setConv] = useState<CampaignConversions | null>(null);
  const [convLoading, setConvLoading] = useState(false);

  async function doArchive() {
    if (!data || archiving) return;
    if (!(await confirmDialog({ title: 'Archivovat kampaň?', confirmLabel: 'Archivovat' }))) return;
    setArchiving(true);
    try { await archiveCampaign(data.id); onBack(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Archivace selhala'); setArchiving(false); }
  }

  // Pozastavit/znovu spustit - u modal kampaní se tím okamžitě vypne/zapne zobrazení
  // modalu (backend syncModalStatus: paused→skrytý, running→active). Reverzibilní.
  async function doToggleStatus(next: 'paused' | 'running') {
    if (!data || statusChanging) return;
    const isModal = data.channels.includes('modal');
    const question = next === 'paused'
      ? { title: 'Pozastavit kampaň?', message: isModal ? 'Modal se přestane okamžitě zobrazovat uživatelům.' : undefined, confirmLabel: 'Pozastavit' }
      : { title: 'Znovu spustit kampaň?', confirmLabel: 'Spustit' };
    if (!(await confirmDialog(question))) return;
    setStatusChanging(true);
    try {
      await updateCampaign(data.id, { status: next });
      const fresh = await getCampaignDetail(data.id);
      setData(fresh);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Změna stavu selhala');
    } finally {
      setStatusChanging(false);
    }
  }

  async function doDuplicate() {
    if (!data || duplicating) return;
    setDuplicating(true);
    try { const { id: newId } = await duplicateCampaign(data.id); onEdit(newId); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Duplikace selhala'); setDuplicating(false); }
  }

  useEffect(() => {
    let cancelled = false;
    getCampaignDetail(id)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (tab !== 'prijemci' || jobs) return;
    let cancelled = false;
    getCampaignJobs(id).then((j) => !cancelled && setJobs(j)).catch(() => undefined);
    return () => { cancelled = true; };
  }, [tab, id, jobs]);

  function loadConversions(refresh = false) {
    setConvLoading(true);
    getCampaignConversions(id, refresh).then(setConv).catch(() => undefined).finally(() => setConvLoading(false));
  }
  useEffect(() => {
    if (tab !== 'konverze' || conv || convLoading) return;
    loadConversions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (data?.status !== 'running' && data?.status !== 'scheduled') return;
    const t = setInterval(() => { getCampaignDetail(id).then(setData).catch(() => undefined); }, 4000);
    return () => clearInterval(t);
  }, [data?.status, id]);

  if (loading) return <PageState title="Načítám detail kampaně" loading />;
  if (error) return <PageState title="Detail kampaně se nepodařilo načíst" message={error} />;
  if (!data) return null;

  const isModal = data.channels.includes('modal');
  const jobRows = (jobs ?? []).map((job) => ({ ...job }));
  type JobRow = (typeof jobRows)[number];
  const jobColumns: DataColumn<JobRow>[] = [
    { key: 'userId', label: 'Uživatel', numeric: true, width: 110, render: (_, job) => `#${job.userId}` },
    { key: 'recipientAddress', label: 'Adresát', width: 220, render: (_, job) => job.recipientAddress ?? '—' },
    { key: 'state', label: 'Stav', width: 125 },
    { key: 'attempts', label: 'Pokusy', numeric: true, width: 90 },
    { key: 'sentAt', label: 'Odesláno', width: 140, render: (_, job) => fmtDate(job.sentAt) },
    { key: 'lastError', label: 'Chyba', width: 250, render: (_, job) => job.lastError ?? '—' },
  ];

  return (
    <section className="marketing-campaign-detail rondo-arc-campaign-detail p1-arc-scope">
      <Button variant="ghost" size="sm" onClick={onBack}>Zpět na seznam</Button>

      <ArcPanel className="rondo-arc-campaign-identity">
        <div className="rondo-arc-campaign-identity-copy">
          <span className="rondo-arc-campaign-identity-icon"><Icon name={(TYPE_META[data.type]?.icon) ?? 'mail'} size={20} /></span>
          <div><h1>{data.name}</h1><div className="rondo-arc-campaign-identity-meta">
            {data.country && <span>{FLAG[data.country] ?? ''} {data.country}</span>}
            <span>{TYPE_META[data.type]?.label ?? data.type}</span>
            {data.channels.map((channel) => <span key={channel}><Icon name={CHANNEL_ICON[channel]} size={12} />{CHANNEL_LABEL[channel]}</span>)}
            {data.subject && <small>{data.subject}</small>}
          </div></div>
        </div>
        <div className="rondo-arc-campaign-identity-side">
          <div className="rondo-arc-campaign-identity-state">{data.sentAt && <small>{isModal ? 'Spuštěno' : 'Odesláno'} {fmtDateTime(data.sentAt)}</small>}<StatusBadge status={data.status} isModal={isModal} /></div>
          <div className="rondo-arc-campaign-identity-actions">
            {(data.status === 'running' || data.status === 'scheduled') && <Button variant="secondary" size="sm" onClick={() => doToggleStatus('paused')} loading={statusChanging}>Pozastavit</Button>}
            {data.status === 'paused' && <Button variant="secondary" size="sm" onClick={() => doToggleStatus('running')} loading={statusChanging}>Znovu spustit</Button>}
            <Button variant="secondary" size="sm" onClick={doDuplicate} loading={duplicating}>Duplikovat</Button>
            {data.status !== 'archived' && <Button variant="danger" size="sm" onClick={doArchive} loading={archiving}>Archivovat</Button>}
          </div>
        </div>
      </ArcPanel>

      {!isModal && (data.status === 'running' || data.status === 'scheduled' || data.status === 'paused') && (() => {
        const total = data.recipients || 0;
        const done = data.jobStates.sent + data.jobStates.failed + data.jobStates.skipped;
        const pct = total ? Math.round((done / total) * 100) : 0;
        return (
          <Card title={data.status === 'scheduled' ? `Naplánováno na ${fmtDateTime(data.scheduledAt)}` : data.status === 'paused' ? 'Pozastaveno' : 'Odesílá se'} className="rondo-arc-campaign-progress">
            <Progress label="Průběh odesílání" value={pct} showValue />
            <p>{data.jobStates.sent.toLocaleString('cs-CZ')} z {total.toLocaleString('cs-CZ')} odesláno</p>
            {(data.jobStates.failed > 0 || data.jobStates.skipped > 0) && (
              <small>{data.jobStates.skipped > 0 && <>Přeskočeno {data.jobStates.skipped}</>}{data.jobStates.failed > 0 && <> · Chyb {data.jobStates.failed}</>}</small>
            )}
          </Card>
        );
      })()}

      <ArcTabs label="Detail kampaně" items={((isModal ? ['prehledy', 'udaje', 'html', 'konverze'] : ['prehledy', 'udaje', 'html', 'prijemci', 'konverze']) as DetailTab[]).map((value) => ({ value, label: { prehledy: 'Přehledy', udaje: 'Údaje', html: data.channels[0] === 'mail' ? 'HTML obsah' : 'Náhled', prijemci: `Příjemci (${data.recipients})`, konverze: conv && !conv.note ? `Konverze (${conv.total_leads})` : 'Konverze' }[value] }))} value={tab} onChange={setTab} className="rondo-arc-campaign-detail-tabs" />

      {tab === 'prehledy' && <CampaignAnalytics data={data} isModal={isModal} />}

      {tab === 'konverze' && (
        <ArcPanel className="rondo-arc-campaign-conversions">
          <div className="rondo-arc-campaign-conversions-head"><h2>Konverze</h2><Button variant="secondary" size="sm" loading={convLoading} onClick={() => loadConversions(true)}>Přepočítat</Button></div>
          {convLoading && !conv && <div className="text-muted py-3">Počítám konverze (volá se Rondo per uživatel)…</div>}
          {conv && conv.note && <div className="alert alert-light border mb-0">{conv.note}</div>}
          {conv && !conv.note && (() => {
            const slugs = Object.keys(conv.by_partner);
            const conversionRows = slugs.map((slug) => {
              const partner = conv.by_partner[slug]!;
              return { slug, clicks: partner.clicks, unique: partner.unique_clickers, leads: partner.leads, cr: partner.unique_clickers ? Math.round((partner.leads / partner.unique_clickers) * 1000) / 10 : null };
            });
            type ConversionRow = (typeof conversionRows)[number];
            const conversionColumns: DataColumn<ConversionRow>[] = [
              { key: 'slug', label: 'Partner', width: 200 },
              { key: 'clicks', label: 'Prokliky', numeric: true, render: (_, row) => row.clicks.toLocaleString('cs-CZ') },
              { key: 'unique', label: 'Unikátní', numeric: true, render: (_, row) => row.unique.toLocaleString('cs-CZ') },
              { key: 'leads', label: 'Konverze', numeric: true, render: (_, row) => row.leads.toLocaleString('cs-CZ') },
              { key: 'cr', label: 'CR', numeric: true, render: (_, row) => row.cr != null ? `${row.cr} %` : '—' },
            ];
            return (
              <>
                <div className="rondo-arc-campaign-conversion-metrics">
                  <MetricCard label="Konverze (leady)" value={conv.total_leads} context="Z přiřazených partnerů" />
                  <MetricCard label="Klikající (celkem)" value={conv.clickers_count} context="Unikátní klikající" />
                </div>
                <div className="rondo-arc-campaign-conversion-table"><SortableDataTable rows={conversionRows} columns={conversionColumns} rowKey="slug" caption="Konverze kampaně podle partnera" emptyMessage="Žádné konverze." /></div>
                <div className="text-muted small mt-2">
                  Konverze = klikající, který udělal lead u partnera <strong>po odeslání kampaně</strong> (zdroj: Rondo karta uživatele).
                  {conv.capped && <span className="text-warning"> · Pozor: spočítáno z prvních 120 klikajících (velká kampaň - přepočet na pozadí TODO).</span>}
                  {conv.synced_at && <> · Aktualizováno {fmtDate(conv.synced_at)}{conv.from_cache ? ' (cache)' : ''}.</>}
                </div>
              </>
            );
          })()}
        </ArcPanel>
      )}

      {tab === 'udaje' && (
        <ArcPanel className="rondo-arc-campaign-data">
          <section className="rondo-arc-campaign-fact-section">
            <h2>Identifikace</h2>
            <dl className="rondo-arc-campaign-facts">
              <CampaignFact label="ID" value={<code>#{data.id}</code>} />
              <CampaignFact label="Název" value={data.name} />
              <CampaignFact label="Typ" value={TYPE_META[data.type]?.label ?? data.type} />
              <CampaignFact label="Stav" value={STATUS_META[data.status].label} />
              <CampaignFact label="Kanál" value={data.channels.map((channel) => CHANNEL_LABEL[channel]).join(', ')} />
            </dl>
          </section>
          <section className="rondo-arc-campaign-fact-section">
            <h2>Cílení</h2>
            <dl className="rondo-arc-campaign-facts">
              <CampaignFact label="Trh" value={data.country ? `${FLAG[data.country] ?? ''} ${data.country}`.trim() : undefined} />
              <CampaignFact label="Segment" value={data.segmentName} />
              {!isModal && <CampaignFact label="Předmět" value={data.subject} />}
            </dl>
          </section>
          <section className="rondo-arc-campaign-fact-section">
            <h2>Termíny</h2>
            <dl className="rondo-arc-campaign-facts">
              <CampaignFact label="Naplánováno" value={fmtDateTime(data.scheduledAt)} />
              <CampaignFact label={isModal ? 'Spuštěno' : 'Odesláno'} value={fmtDateTime(data.sentAt)} />
              <CampaignFact label="Vytvořeno" value={fmtDateTime(data.createdAt)} />
            </dl>
          </section>
        </ArcPanel>
      )}

      {tab === 'html' && (() => {
        const ch = data.channels[0];
        const meta = (data.meta ?? {}) as { modal?: { size?: string; theme?: string; dismissText?: string | null; body?: string }; sms?: { body?: string }; notif?: { message?: string; url?: string } };
        const sample = (s: string) => String(s ?? '').replace(/\[%\s*shared\.([A-Z0-9_]+)\s*-?%\]/gi, (_m, k: string) => (({ VOCATIVE: 'Danieli', P1: 'Danieli', FIRST_NAME: 'Daniel', LAST_NAME: 'Trojan', EMAIL: 'daniel@example.com', GREETING: 'Dobrý den' } as Record<string, string>)[k] ?? (k.endsWith('_URL') ? '#' : '…')));
        if (ch === 'mail') {
          return (
            <ArcPanel className="rondo-arc-campaign-preview">
              {data.htmlBody
                ? <iframe title="Náhled HTML" sandbox="allow-same-origin" srcDoc={sample(data.htmlBody)} style={{ width: '100%', height: 600, border: '1px solid #eef0f3', borderRadius: 8 }} />
                : <div className="text-muted">Žádný HTML obsah.</div>}
            </ArcPanel>
          );
        }
        if (ch === 'modal' && meta.modal?.body) {
          const md = meta.modal;
          const dark = md.theme === 'dark';
          // modal s vlastnim pozadim pres celou plochu - karta nesmi pridavat bily ram
          const mdFull = String(md.body ?? '').includes('id="pf-full"');
          const pw = md.size === 's' ? 380 : md.size === 'l' ? 560 : 440;
          return (
            <ArcPanel className="rondo-arc-campaign-preview">
              <style>{`.pfd iframe{display:block;width:100%}.pfd .pf-mo-text:last-child{margin-bottom:0}.pfd,.pfd *{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif}.pfd{font-size:16px}.pfd .pf-mo-img{margin-bottom:16px}.pfd .pf-mo-img img{max-width:100%;max-height:200px;border-radius:8px;display:block;margin:0 auto}.pfd .pf-mo-title{font-size:19px;font-weight:800;margin-bottom:8px;line-height:1.25}.pfd .pf-mo-text{font-size:14px;line-height:1.5;margin-bottom:14px;text-align:left}.pfd .pf-mo-cta{display:block;background:#007d88;color:#fff;text-align:center;font-weight:700;font-size:16px;padding:12px 22px;border-radius:10px;text-decoration:none;margin-top:8px}`}</style>
              <div style={{ background: '#6b7280', borderRadius: 12, padding: 24, display: 'flex', justifyContent: 'center' }}>
                <div className="pfd" style={{ width: '100%', maxWidth: pw, borderRadius: 16, position: 'relative', boxShadow: '0 18px 50px rgba(0,0,0,.35)', background: mdFull ? 'transparent' : (dark ? '#171b24' : '#fff'), color: dark ? '#e5e7eb' : '#1f2937', padding: mdFull ? 0 : 24, overflow: mdFull ? 'hidden' : undefined }}>
                  <button type="button" style={{ position: 'absolute', top: 8, right: 10, border: 'none', background: 'transparent', fontSize: 18, color: '#9ca3af', cursor: 'default' }}>✕</button>
                  <SandboxHtml html={sample(md.body ?? '')} css={`body,body *{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif}body{font-size:16px;color:${dark ? '#e5e7eb' : '#1f2937'}}.pf-mo-img{margin-bottom:16px}.pf-mo-img img{max-width:100%;max-height:200px;border-radius:8px;display:block;margin:0 auto}.pf-mo-title{font-size:19px;font-weight:800;margin-bottom:8px;line-height:1.25}.pf-mo-text{font-size:14px;line-height:1.5;margin-bottom:14px;text-align:left}.pf-mo-text:last-child{margin-bottom:0}.pf-mo-cta{display:block;background:#007d88;color:#fff;text-align:center;font-weight:700;font-size:16px;padding:12px 22px;border-radius:10px;text-decoration:none;margin-top:8px}`} />
                  {md.dismissText && <div style={{ marginTop: 12, fontSize: '.78rem', color: '#9ca3af', textDecoration: 'underline', textAlign: 'center' }}>{md.dismissText}</div>}
                </div>
              </div>
              <div className="text-muted small mt-2">Náhled modalu ({pw} px). Vokativ ukázkově „Danieli".</div>
            </ArcPanel>
          );
        }
        // SMS / NOTIF - text
        if (ch === 'sms') {
          return <ArcPanel className="rondo-arc-campaign-preview"><div className="text-muted small mb-1">Text SMS:</div><div style={{ maxWidth: 360, background: '#e7f3ff', borderRadius: 14, padding: '10px 14px', fontSize: '.9rem', whiteSpace: 'pre-wrap' }}>{sample(meta.sms?.body ?? '') || '-'}</div></ArcPanel>;
        }
        if (ch === 'notif') {
          return <ArcPanel className="rondo-arc-campaign-preview"><div className="text-muted small mb-1">Notifikace:</div><div style={{ maxWidth: 380, border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: '.9rem' }}>{sample(meta.notif?.message ?? '') || '-'}{meta.notif?.url ? <div className="text-muted small mt-1" style={{ wordBreak: 'break-all' }}>{meta.notif.url}</div> : null}</div></ArcPanel>;
        }
        return <ArcPanel className="rondo-arc-campaign-preview"><div className="text-muted">Žádný obsah.</div></ArcPanel>;
      })()}

      {tab === 'prijemci' && (
        <div className="rondo-arc-campaign-jobs-table">{jobs == null ? <PageState title="Načítám příjemce" loading /> : <SortableDataTable rows={jobRows} columns={jobColumns} rowKey="id" caption="Příjemci kampaně" emptyMessage="Zatím žádní příjemci." />}</div>
      )}
    </section>
  );
}

function CampaignAnalytics({ data, isModal }: { data: CampaignDetail; isModal: boolean }) {
  const isMail = data.channels.includes('mail');
  const sent = data.jobStates.sent || data.recipients;
  const stages = isModal
    ? [
        { label: 'Prokliky', value: data.clicksTotal, tone: 'orange' },
        { label: 'Unikátní reakce', value: data.uniqueClickers, tone: 'green' },
      ]
    : [
        { label: 'Příjemci', value: data.recipients, tone: 'ink' },
        { label: 'Odesláno', value: sent, tone: 'green' },
        ...(isMail ? [{ label: 'Doručeno', value: data.deliveredTotal, tone: 'sage' }, { label: 'Otevřeno', value: data.opensUnique, tone: 'orange' }] : []),
        { label: 'Kliknuto', value: data.uniqueClickers, tone: 'violet' },
      ];
  const max = Math.max(...stages.map((stage) => stage.value), 1);
  const openRate = data.deliveredTotal ? Math.min(100, (data.opensUnique / data.deliveredTotal) * 100) : 0;
  const deliveryRate = data.recipients ? Math.min(100, (data.deliveredTotal / data.recipients) * 100) : 0;
  const bounceRate = data.recipients ? Math.min(100, (data.bouncesTotal / data.recipients) * 100) : 0;
  const metrics = isModal
    ? [
        { label: 'CTR', value: `${data.ctr ?? 0} %`, note: 'míra reakce', icon: 'trending-up' },
        { label: 'Unikátní kliky', value: data.uniqueClickers.toLocaleString('cs-CZ'), note: `${data.clicksTotal.toLocaleString('cs-CZ')} kliků celkem`, icon: 'target' },
      ]
    : [
        { label: 'Doručitelnost', value: `${deliveryRate.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`, note: `${data.deliveredTotal.toLocaleString('cs-CZ')} doručeno`, icon: 'check-circle' },
        { label: 'Open rate', value: `${openRate.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`, note: `${data.opensUnique.toLocaleString('cs-CZ')} unikátních`, icon: 'mail' },
        { label: 'CTR', value: `${data.ctr ?? 0} %`, note: `${data.uniqueClickers.toLocaleString('cs-CZ')} klikajících`, icon: 'mouse-pointer' },
      ];
  return (
    <div className="rondo-arc-campaign-analytics">
      <div className="rondo-arc-campaign-analytics-head"><h2>{isModal ? 'Reakce uživatelů' : 'Cesta od odeslání ke kliknutí'}</h2><p>{data.sentAt ? `Data od ${fmtDateTime(data.sentAt)}` : 'Průběžná data kampaně'}</p></div>
      <div className="rondo-arc-campaign-analytics-layout">
        <Card title="Výkonnostní cesta" className="rondo-arc-campaign-funnel">
          <div className="rondo-arc-campaign-result"><span>Hlavní výsledek</span><strong>{(isModal ? data.uniqueClickers : data.deliveredTotal).toLocaleString('cs-CZ')}</strong><small>{isModal ? 'unikátních reakcí' : 'doručených zpráv'}</small></div>
          <div className="rondo-arc-campaign-stage-list">{stages.map((stage) => <div key={stage.label}><div><span>{stage.label}</span><strong>{stage.value.toLocaleString('cs-CZ')}</strong></div><Progress value={stage.value} max={max} label={stage.label} className={`tone-${stage.tone}`} /></div>)}</div>
        </Card>
        <div className="rondo-arc-campaign-rate-grid">
          {metrics.map((metric) => <Card title={metric.label} className="rondo-arc-campaign-rate" key={metric.label}><strong>{metric.value}</strong><p>{metric.note}</p></Card>)}
          {isMail && <Card title="Kvalita rozesílky" className="rondo-arc-campaign-rate"><Progress value={Math.max(0, 100 - bounceRate)} label="Úspěšné doručení" showValue /><p>{data.bouncesTotal.toLocaleString('cs-CZ')} nedoručeno · {data.unsubscribesTotal.toLocaleString('cs-CZ')} odhlášení</p></Card>}
        </div>
      </div>
    </div>
  );
}

function CampaignFact({ label, value }: { label: string; value: ReactNode }) {
  const empty = value == null || value === '' || value === '-';
  return (
    <div className="rondo-arc-campaign-fact">
      <dt>{label}</dt>
      <dd>{empty ? <span className="muted">—</span> : value}</dd>
    </div>
  );
}
